/**
 * Global error boundary (§1.2 "No dead ends"): a human explanation, a next action and a support reference
 * the participant can quote. The reference is a short random id generated locally (never a token) and logged
 * with the error so support can correlate.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, ErrorState } from '@windrop/ui';
import { getMessagesSync } from '../lib/i18n';
import { formatMessage } from '@windrop/ui';

interface Props {
  children: ReactNode;
  locale?: string;
  supportHref?: string;
}
interface State {
  error: Error | null;
  reference: string;
}

export function newSupportReference(): string {
  const c = globalThis.crypto;
  const id = c?.randomUUID ? c.randomUUID() : `${Date.now().toString(36)}`;
  return `WEB-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, reference: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reference: newSupportReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[windrop] render error', this.state.reference, error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    const m = getMessagesSync(this.props.locale ?? 'en');
    const t = (k: string, fallback: string) => formatMessage(m[k] ?? fallback);
    return (
      <main id="main" className="page page--narrow" tabIndex={-1}>
        <ErrorState
          title={t('error.title', 'Something went wrong')}
          body={t('error.body', 'This page could not be shown. Nothing you entered has been lost.')}
          reference={this.state.reference}
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t('error.reload', 'Reload this page')}
            </Button>
          }
          secondaryAction={
            <Button variant="secondary" href={this.props.supportHref ?? '/support'}>
              {t('error.support', 'Contact support')}
            </Button>
          }
        />
      </main>
    );
  }
}
